/**
 * PlanChecklist — the case plan as a checklist that ticks as steps finish. A step named by
 * `flashStep` (a voice control: "go back to step 4") flashes for 2 s; steps with recorded results
 * carry a "Recorded" chip (the one-key fallback, SPEC §7).
 */

import { useEffect, useState } from 'react';
import {
  CircleCheck, Circle, LoaderCircle, Hand, SkipForward, CircleX, Rewind,
} from 'lucide-react';
import SpecialistDot from './SpecialistDot.jsx';
import './AgentStream.css';

const FLASH_MS = 2000;

const STEP_STATUS = {
  done: { Icon: CircleCheck, label: 'Done', tone: 'good' },
  running: { Icon: LoaderCircle, label: 'Running', tone: 'accent', spin: true },
  waiting: { Icon: Hand, label: 'Waiting for you', tone: 'decide' },
  skipped: { Icon: SkipForward, label: 'Skipped', tone: 'faint' },
  failed: { Icon: CircleX, label: 'Failed', tone: 'bad' },
  pending: { Icon: Circle, label: 'Pending', tone: 'faint' },
};

function StepIcon({ status }) {
  const s = STEP_STATUS[status] ?? STEP_STATUS.pending;
  const StatusIcon = s.Icon;
  return (
    <span className={`astream__plan-icon is-${s.tone}${s.spin ? ' is-spin' : ''}`} title={s.label}>
      <StatusIcon size={14} strokeWidth={2} aria-hidden="true" />
      <span className="agent-sr">{s.label}</span>
    </span>
  );
}

export default function PlanChecklist({
  steps = [], flashStep = null, specialists = {}, onGoto, onSkip, readOnly = false,
}) {
  const flashKey = flashStep ? `${flashStep.stepId}|${flashStep.at}` : null;
  const [expiredFlash, setExpiredFlash] = useState(null);

  useEffect(() => {
    if (!flashKey) return undefined;
    const timer = setTimeout(() => setExpiredFlash(flashKey), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashKey]);
  const flashId = flashKey && flashKey !== expiredFlash ? flashStep.stepId : null;

  if (!steps.length) return <p className="astream__empty">No plan yet.</p>;
  const doneCount = steps.filter((s) => s.status === 'done' || s.status === 'skipped').length;

  return (
    <div className="astream__plan">
      <div className="astream__plan-head">
        <span className="astream__kicker">Plan</span>
        <span className="astream__plan-count">{doneCount}/{steps.length}</span>
      </div>
      <ol className="astream__plan-list">
        {steps.map((step) => {
          const canGoto = !readOnly && typeof onGoto === 'function' && (step.status === 'done' || step.status === 'skipped');
          const canSkip = !readOnly && typeof onSkip === 'function' && step.status === 'pending';
          return (
            <li
              key={step.id}
              className={`astream__step is-${step.status}${flashId === step.id ? ' is-flash' : ''}`}
            >
              <StepIcon status={step.status} />
              <div className="astream__step-main">
                <div className="astream__step-title">
                  <span className="astream__step-no">{step.index}</span>
                  <span>{step.title}</span>
                  <SpecialistDot specialist={specialists[step.specialist]} size={6} />
                  {step.recorded_available && <span className="astream__chip-tag" title="A recorded result can stand in for this step (Shift+F)">Recorded</span>}
                </div>
                {(step.summary || step.detail) && (
                  <div className="astream__step-detail">{step.status === 'done' ? step.summary || step.detail : step.detail || step.summary}</div>
                )}
              </div>
              {(canGoto || canSkip) && (
                <div className="astream__step-actions">
                  {canGoto && (
                    <button type="button" className="astream__icon-btn" title={`Go back to step ${step.index}`} onClick={() => onGoto(step.index)}>
                      <Rewind size={12} aria-hidden="true" />
                    </button>
                  )}
                  {canSkip && (
                    <button type="button" className="astream__icon-btn" title={`Skip step ${step.index}`} onClick={() => onSkip(step.id)}>
                      <SkipForward size={12} aria-hidden="true" />
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
